import React, { useEffect, useRef } from "react";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import CustomSelectField from "../../formik/CustomSelectField.tsx";
import { PanelBody } from "../../panel/panel.jsx";
import SheduleComponent from "./SheduleComponent.tsx";
import ScheduleCompactDate from "./ScheduleCompactDate.tsx";
import {
  readHoursByBranch,
  readPositionsByBranchAndShift,
  readSchedulesByBranchShiftWeekDay,
} from "../../../services/branchSchedule.service.ts";
import { shifts, weekDays, generateTimeIntervals } from "../../../utils/constanst.js";
import FullScreenContainer from "../../FullScreenContainer.tsx";
import FullScreenSchedule from "./FullScreenSchedule.tsx";
import Loader from "../../Loader.tsx";
import moment from "moment";
import { readBranches } from "../../../services/branch.service.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/index.ts";
import { setRefreshScheduleFunction } from "../../../store/slices/scheduleSlice.ts";
import { defaultFilters } from "../../../models/ColorSchedule.ts";

dayjs.locale("es");

const getStartOfWeek = (date: Date) => {
  const newDate = new Date(date);
  const day = newDate.getDay();
  const diff = newDate.getDate() - day + (day === 0 ? -6 : 1);
  newDate.setDate(diff);
  return newDate;
};

const getEndOfWeek = (date: Date) => {
  const startOfWeek = getStartOfWeek(new Date(date));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return endOfWeek;
};

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getWeekRange = (date: Date) => {
  const startOfWeek = getStartOfWeek(new Date(date));
  const endOfWeek = getEndOfWeek(new Date(date));
  return `${formatDate(startOfWeek)} a ${formatDate(endOfWeek)}`;
};

const getDayOfWeekFromDate = (date: Date) => {
  const day = date.getDay();

  return day === 0 ? 6 : day - 1;
};

const getDayNameFromDate = (date: Date) => {
  const dayIndex = getDayOfWeekFromDate(date);
  return weekDays[dayIndex]?.name || "Lunes";
};

const HorarioSucuralFormDate = ({ formik, firstRender, setFirstRender, showCompactView, setShowCompactView }) => {
  const [loading, setLoading] = React.useState(false);
  const [positionsArray, setPositionsArray] = React.useState<any>([]);
  const [existingsSchedules, setExistingsSchedules] = React.useState<any>([]);
  const [positionsSorted, setPositionsSorted] = React.useState<any>([]);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [hours, setHours] = React.useState<any>([]);
  const [hoursRange, setHoursRange] = React.useState<any[]>([]);
  const [branches, setBranches] = React.useState<any>([]);
  const [compactFilters, setCompactFilters] = React.useState({
    resumen: true,
    assistence: false,
    late: false,
    withoutAssistence: false,
    fueraDeHorario: false,
    sinHorario: false,
  });
  const dispatch = useDispatch();

  const { user } = useSelector((state: RootState) => state.auth);

  const calculateWeekAndDay = React.useCallback((selectedDate: Date) => {
    const weekRange = getWeekRange(selectedDate);
    const dayOfWeek = getDayOfWeekFromDate(selectedDate);
    const dayName = getDayNameFromDate(selectedDate);

    return {
      weekRange,
      dayOfWeek,
      dayName,
    };
  }, []);

  const loadDependeicies = async () => {
    try {
      setLoading(true);
      if (
        user.rol === "Administrador" ||
        user.rol === "Líder de líderes" ||
        user.rol === "Administrador-"
      ) {
        const branches = await readBranches();
        setBranches(branches);
      }
      const values = formik.values;

      if (!values.selectedDate) {
        setLoading(false);
        return;
      }

      const selectedDate = new Date(values.selectedDate);
      const { weekRange, dayOfWeek, dayName } =
        calculateWeekAndDay(selectedDate);

      const shift =
        shifts.find((shift) => shift.id === values.shift)?.name ?? "";

      if (positionsArray.length === 0) {
        const positions = await readPositionsByBranchAndShift(
          values.branchId,
          shift
        );

        const newPositionsArray = positions.map((position) => {
          const totalWorkersRequired = position.numberOfWorkers;
          const workers: any = [];

          for (let i = 0; i < totalWorkersRequired; i++) {
            workers.push({
              id: i,
              positionId: position.position.id,
              positionName: position.position.name + " " + (i + 1),
              originalPositionName: position.position.name,
              department:
                position.position?.department?.name || "Sin Departamento",
              shift: position.shift,
              positionOrder: position.position.order,
              positionisDeleted: position.position.isDeleted,
              positionOriginalId_: position.position.id,
              positionShowSchedule: position.showSchedule,
              positionIsEssencial: position.isEssencial,
            });
          }
          return workers;
        });
        setPositionsArray(newPositionsArray.flat());

        const shiftOrder = ["Matutino", "Intermedio", "Vespertino"];

        const sorted = newPositionsArray
          .flat()
          .filter((position) => position.positionShowSchedule)
          .sort((a, b) => {
            const shiftComparison =
              shiftOrder.indexOf(a.shift) - shiftOrder.indexOf(b.shift);
            if (shiftComparison !== 0) return shiftComparison;

            const essentialComparison = (b.positionIsEssencial ? 1 : 0) - (a.positionIsEssencial ? 1 : 0);
            if (essentialComparison !== 0) return essentialComparison;

            const positionOrderComparison = a.positionOrder - b.positionOrder;
            if (positionOrderComparison !== 0) return positionOrderComparison;

            const departmentComparison = a.department.localeCompare(
              b.department
            );
            if (departmentComparison !== 0) return departmentComparison;

            return a.positionName.localeCompare(b.positionName);
          });

        const resultArray: any = [];
        let currentShift = "";
        let currentDepartment = "";

        sorted.forEach((position) => {
          if (position.shift !== currentShift) {
            currentShift = position.shift;
            currentDepartment = "";
            resultArray.push({ title: currentShift, shift: currentShift });
          }

          if (position.department !== currentDepartment) {
            currentDepartment = position.department;
          }

          resultArray.push(position);
        });

        setPositionsSorted(resultArray);
      }

      if (values.shift === -99 || dayOfWeek === -99 || !values.selectedDate) {
        setLoading(false);
        return;
      }

      setFirstRender(false);

      const schedules = await readSchedulesByBranchShiftWeekDay(
        values.branchId,
        shift,
        weekRange,
        dayName
      );

      setExistingsSchedules(schedules);

      const branchHours = await readHoursByBranch(values.branchId);
      setHours(branchHours);

      // Generar hoursRange para la vista compacta
      if (branchHours) {
        const hoursArray = await generateTimeIntervals(
          branchHours?.matutinoStart,
          branchHours?.vespertinoEnd
        );
        hoursArray.unshift(
          { key: -99, display: "Hora" },
          { key: -99, display: "Hora" }
        );
        setHoursRange(hoursArray);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      formik.values.selectedDate &&
      formik.values.shift !== -99 &&
      formik.values.branchId !== -99
    ) {
      if (firstRender) {
        loadDependeicies().then(() => {
          setLoading(false);
          dispatch(
            setRefreshScheduleFunction(async () => await loadDependeicies())
          );
          setFirstRender(false);
        });
      } else {
        loadDependeicies().then(() => {
          setLoading(false);
        });
      }
    }
  }, [formik.values.selectedDate, formik.values.shift, formik.values.branchId]);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      const selectedDate = date.toDate();
      const { dayOfWeek, dayName, weekRange } =
        calculateWeekAndDay(selectedDate);

      formik.setFieldValue("selectedDate", selectedDate.toISOString());
      formik.setFieldValue("dayOfWeek", dayOfWeek);
      formik.setFieldValue("dayName", dayName);
      formik.setFieldValue("weekRange", weekRange);

      if (formik.values.shift === -99) {
        formik.setFieldValue("shift", shifts[3].id);
      }
    } else {
      formik.setFieldValue("selectedDate", null);
      formik.setFieldValue("dayOfWeek", -99);
      formik.setFieldValue("dayName", "");
      formik.setFieldValue("weekRange", "");
    }
  };

  const setTodaySchedule = () => {
    const today = new Date();
    const dayjsToday = dayjs(today);
    handleDateChange(dayjsToday);
    if (formik.values.shift === -99) {
      formik.setFieldValue("shift", shifts[3].id);
    }
  };

  const selectedDateValue = formik.values.selectedDate
    ? dayjs(formik.values.selectedDate)
    : null;

  return (
    <PanelBody>
      <Loader isLoading={loading} />
      {["Administrador", "Administrador-"].includes(user.rol) && (
        <CustomSelectField
          formik={formik}
          field="branchId"
          label="Sucursal"
          list={branches}
          fieldName="branchName"
          sm={3}
        />
      )}

      <div className={`row mb-4 fs-13px col-md-12`}>
        <label
          className={`form-label col-form-label col-md-3 text-end`}
          style={{
            color:
              formik.errors.selectedDate && formik.touched.selectedDate
                ? "red"
                : "inherit",
          }}
        >
          Fecha *
        </label>
        <div className="col-md-3">
          <ConfigProvider locale={esES}>
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              value={selectedDateValue}
              onChange={handleDateChange}
              placeholder="Selecciona una fecha"
            />
          </ConfigProvider>
          {formik.errors.selectedDate && formik.touched.selectedDate && (
            <div className="invalid-feedback d-block">
              {formik.errors.selectedDate}
            </div>
          )}
        </div>
      </div>

      <div className={`row mb-4 fs-13px col-md-12`}>
        <label
          className={`form-label col-form-label col-md-3 text-end`}
          style={{
            color:
              formik.errors.shift && formik.touched.shift ? "red" : "inherit",
          }}
        >
          Turno *
        </label>
        <div className={`col-md-9`} role="group">
          <div className="btn-group">
            {shifts.map((shift) => (
              <button
                type="button"
                key={shift.id}
                className={`btn ${
                  formik.values.shift === shift.id
                    ? "btn-primary"
                    : "btn-outline-secondary"
                } 
                    ${
                      formik.errors.shift && formik.touched.shift
                        ? "btn-outline-danger"
                        : ""
                    }`}
                onClick={() => formik.setFieldValue("shift", shift.id)}
              >
                {shift.name}
              </button>
            ))}
          </div>
          {formik.errors.shift && formik.touched.shift && (
            <div className="invalid-feedback d-block">
              {formik.errors.shift}
            </div>
          )}
        </div>
      </div>

      {/* Botón para cambiar entre vista detallada y compacta */}
      {formik.values.shift !== -99 &&
        formik.values.selectedDate &&
        formik.values.dayOfWeek !== -99 &&
        hours?.id &&
        positionsArray &&
        positionsArray.length > 0 && (
          <div className="row mb-4 fs-13px col-md-12">
            <label className="form-label col-form-label col-md-3 text-end" />
            <div className="col-md-9" role="group">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCompactView(!showCompactView);
                  if (!showCompactView) {
                    setCompactFilters({ ...defaultFilters, resumen: true });
                  }
                }}
              >
                {showCompactView ? "Vista detallada" : "Vista compacta"}
              </button>
            </div>
          </div>
        )}

      {/* Filtros de vista compacta */}
      {showCompactView &&
        formik.values.shift !== -99 &&
        formik.values.selectedDate &&
        formik.values.dayOfWeek !== -99 &&
        hours?.id &&
        positionsArray &&
        positionsArray.length > 0 && (
          <div className="row mb-4 fs-13px col-md-12">
            <label className="form-label col-form-label col-md-3 text-end" />
            <div className="col-md-9 d-flex flex-wrap gap-2" role="group">
              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({ ...defaultFilters, resumen: true })
                }
                style={{
                  ...(!compactFilters.resumen && {
                    opacity: 0.5,
                  }),
                }}
              >
                Resumen
              </button>

              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({ ...defaultFilters, assistence: true })
                }
                style={{
                  ...(!compactFilters.assistence && {
                    opacity: 0.5,
                  }),
                }}
              >
                Asistencias
              </button>

              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({ ...defaultFilters, late: true })
                }
                style={{
                  ...(!compactFilters.late && {
                    opacity: 0.5,
                  }),
                }}
              >
                Retardo
              </button>

              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({
                    ...defaultFilters,
                    withoutAssistence: true,
                  })
                }
                style={{
                  ...(!compactFilters.withoutAssistence && {
                    opacity: 0.5,
                  }),
                }}
              >
                Sin asistencia
              </button>

              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({
                    ...defaultFilters,
                    fueraDeHorario: true,
                  })
                }
                style={{
                  ...(!compactFilters.fueraDeHorario && {
                    opacity: 0.5,
                  }),
                }}
              >
                Fuera de horario
              </button>

              <button
                className="btn btn-azul"
                onClick={() =>
                  setCompactFilters({
                    ...defaultFilters,
                    sinHorario: true,
                  })
                }
                style={{
                  ...(!compactFilters.sinHorario && {
                    opacity: 0.5,
                  }),
                }}
              >
                Sin horario programado
              </button>
            </div>
          </div>
        )}

      {fullScreen && (
        <FullScreenContainer
          open={fullScreen}
          tittle="Horario de la sucursal"
          onClsFn={() => setFullScreen(false)}
        >
          <FullScreenSchedule
            existingsSchedules={existingsSchedules}
            positionsArray={
              formik.values.shift === 3 ? positionsSorted : positionsArray
            }
            formik={formik}
            turno={formik.values.shift}
            setPositionsArray={setPositionsArray}
            selectedDay={formik.values.dayOfWeek}
          />
        </FullScreenContainer>
      )}
      {formik.values.shift !== -99 &&
        formik.values.selectedDate &&
        formik.values.dayOfWeek !== -99 &&
        (hours?.id ? (
          positionsArray && positionsArray.length > 0 ? (
            showCompactView ? (
              <ScheduleCompactDate
                hours={hours}
                formik={formik}
                positionsArray={
                  formik.values.shift === 3 ? positionsSorted : positionsArray
                }
                selectedDate={formik.values.selectedDate}
                turno={formik.values.shift}
                hoursRange={hoursRange}
                compactFilters={compactFilters}
              />
            ) : (
              <SheduleComponent
                selectedWeek={formik.values.weekRange}
                hours={hours}
                existingsSchedules={existingsSchedules}
                positionsArray={
                  formik.values.shift === 3 ? positionsSorted : positionsArray
                }
                formik={formik}
                turno={formik.values.shift}
                setPositionsArray={setPositionsArray}
                selectedDay={formik.values.dayOfWeek}
                readOnly={true}
              />
            )
          ) : (
            <p className="text-center mt-5">
              No hay puestos disponibles para el turno seleccionado
            </p>
          )
        ) : (
          <div className="row d-flex justify-content-between w-50 mx-auto mt-5">
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">¡Atención!</h4>
              <p>La sucursal no cuenta con un horario establecido.</p>
              <hr />
              <p className="mb-0">
                Por favor, completa el horario de la sucursal para poder asignar
                los trabajadores.
              </p>
            </div>
          </div>
        ))}
    </PanelBody>
  );
};

export default HorarioSucuralFormDate;
